/**
 * routes/index — **สารบัญของโฟลเดอร์นี้** · 2 หน้าที่:
 *   1. ตารางเดียวที่แม็ป path → route (`appRoutes()`)
 *   2. **เจ้าของสัญญา `RouteDeps`** — ของที่ชั้น route ต้องการจากภายนอก
 *
 * 🔑 **ทำไม `RouteDeps` อยู่ที่นี่ ไม่ใช่ที่ `composition/`** (owner ทักตอนออกแบบ · PLAN §1 ข้อ 4)
 *    ผู้ผลิตประกาศสัญญาให้ผู้บริโภค = **กลับทิศ** และสวนกับที่ทั้ง repo ทำอยู่แล้ว
 *    (`feature-activities/types/*.port.ts` = ฝั่งที่ *ใช้* เป็นคนประกาศ)
 *    ⇒ ที่นี่ประกาศว่า *"ชั้น route ต้องการอะไร"* แล้ว `composition/` เป็นฝ่ายไปหามาให้
 *
 * 🔑 **`index.*` = ไฟล์เดียวในโฟลเดอร์ที่ import เพื่อนบ้านได้** ที่เหลือเป็นใบไม้
 *    (`.dependency-cruiser.cjs` กฎ `no-sibling-import-outside-index` บังคับ)
 *    ⇒ เปิดโฟลเดอร์แล้วรู้ทันทีว่าไฟล์ไหนเป็นทางเข้า **โดยไม่ต้องอ่านคอมเมนต์**
 *
 * 🔴 **route component รับเฉพาะ prop ที่ตัวเองใช้ ไม่ใช่ `deps` ทั้งก้อน**
 *    วัดตอนออกแบบ: ไม่มี route ไหนใช้เกิน 4 จาก 9 field ⇒ ส่งทั้งก้อน = ทุกหน้า
 *    ผูกกับทุก port แล้วไฟล์ `*.route.tsx` **ไม่มีทาง import อะไรจาก `composition/` เลย**
 *    ซึ่งทำให้ `composition/` เป็น scope ปิดจริง (ตรวจได้ด้วย grep)
 *
 * 🔴 **2 path นี้ผูกกับของนอกโค้ด เปลี่ยนไม่ได้อิสระ:**
 *   · `VERIFY_EMAIL_PATH` — ลิงก์ในเมลที่ส่งออกไปแล้วชี้มาที่นี่ (backend ประกอบจาก
 *     `WEB_BASE_URL` + path นี้) ⇒ เปลี่ยน = ลิงก์เก่าตายทั้งหมด
 *   · `LOGIN_PATH` — ทางเข้าของผู้ใช้ที่ไม่ได้มาจาก LINE
 *   ⇒ อ่านค่าคงที่จาก feature lib **ห้าม hardcode string ซ้ำที่นี่**
 *
 * ═══ route-level lazy (ADR-008 · 2026-08-05) ════════════════════════════════
 * แต่ละหน้าโหลดตอนเข้าถึงจริงผ่าน **`lazy` ของ React Router** ไม่ใช่ `React.lazy`
 * — router จัดการสถานะ "กำลังโหลด" ให้เอง (ไม่ต้องห่อ `<Suspense>` ทีละที่)
 * และ **ยัง inject deps ได้** เพราะ `lazy` คืน `element` ได้ ไม่ใช่แค่ `Component`
 *
 * 🔴 **กติกาที่ห้ามผิด: specifier ของ `import()` ต้องเป็น string literal เสมอ**
 *    ห้ามประกอบ path จากตัวแปร/template string — bundler resolve ตอน build ไม่ได้
 *    ⇒ path ผิดจะ **เงียบจนกว่าจะมีคนเปิดหน้านั้นจริง** แทนที่จะแดงตอน build
 *    นี่คือด่านชั้นที่ 1 ของ 3 ชั้นใน `ADR-008` — และเป็นชั้นที่ถูกที่สุด
 *    (พิสูจน์แล้ว: พิมพ์ `verify-emai.js` → `vite build` แดงทันทีด้วย `Could not resolve`)
 *
 * ℹ️ **`not-found` ตั้งใจไม่ lazy** — เป็นหน้าที่ต้องขึ้นได้เสมอ รวมถึงตอนที่ chunk
 *    ของหน้าอื่นโหลดไม่สำเร็จ ⇒ ทำให้ lazy = พึ่งเครือข่ายรอบสองในจังหวะที่เพิ่งพลาดรอบแรก
 */
import { redirect, type RouteObject } from 'react-router';
import {
  ACTIVITIES_PATH,
  ACTIVITY_DETAIL_PATH,
  type ActivitiesApiPort,
  type ClipboardPort,
  type SharePort,
} from '@theelite/frontend-member-feature-activities';
import {
  LOGIN_PATH,
  VERIFY_EMAIL_PATH,
  type AuthApiPort,
  type GoogleIdentityPort,
  type LiffPort,
  type NavigationPort,
} from '@theelite/frontend-member-feature-auth';
import { NotFoundRoute } from './not-found.route.js';

/**
 * สิ่งที่ชั้น route ต้องการจากภายนอก — **`composition/` เป็นคนหามาให้**
 *
 * ℹ️ ทุก field เป็น port ของ feature หรือค่าธรรมดา · **ไม่มี type ของ adapter ตัวไหน
 *    โผล่มาที่นี่เลย** ⇒ เปลี่ยน adapter (LIFF → LINE redirect · GIS → อย่างอื่น)
 *    ไม่กระทบไฟล์นี้แม้แต่บรรทัดเดียว
 */
export type RouteDeps = {
  liff: LiffPort;
  /**
   * `undefined` = **ช่องทาง Google ปิดอยู่** (ไม่มี `googleClientId` ใน `/config.js`)
   * route `/login` เป็นคนบอกผู้ใช้ — feature อื่นไม่ต้องรู้เรื่องนี้
   */
  google: GoogleIdentityPort | undefined;
  authApi: AuthApiPort;
  activitiesApi: ActivitiesApiPort;
  clipboard: ClipboardPort;
  share: SharePort;
  navigation: NavigationPort;
  /** เก็บ access token ที่เพิ่ง login ได้ — route ไม่ต้องรู้ว่าเก็บที่ไหน */
  onAuthenticated: (accessToken: string) => void;
  /**
   * origin ของเว็บตัวเอง — ใช้ประกอบ **ลิงก์เชิญเพื่อนที่ถูกแชร์ออกไปนอกระบบ**
   * ⚠️ เป็น **ค่า** ไม่ใช่การอ่าน `window.location` ที่นี่ · แอปเป็นคนหามาให้
   */
  webOrigin: string;
};

export function appRoutes(deps: RouteDeps): RouteObject[] {
  return [
    /**
     * 🔄 **เปลี่ยน 2026-08-08 — `/` ไม่ใช่ LIFF entry อีกแล้ว**
     *
     * เดิม `/` ชี้ `LiffEntryRoute` เหมือน `/liff` ด้วยเหตุผลว่า *"ผู้ใช้จาก Rich Menu
     * อาจมาถึงด้วย path ไหนก็ได้แล้วแต่ config ฝั่ง LINE"* ⇒ เป็นการเอา **edge case
     * (กลัว config ฝั่ง LINE ตั้งผิด)** มาทับ **happy path (คนเปิดเว็บตรง ๆ)**
     *
     * 🔴 ผลที่เกิดจริง (owner เจอเอง 2026-08-08): เปิด `app.staging.theeliteinc.co`
     *    จากเบราว์เซอร์เดสก์ท็อป → `liff.init()` ล้มเพราะไม่ได้อยู่ในแอป LINE →
     *    ขึ้น "เข้าสู่ระบบไม่สำเร็จ" **และไม่มีทางไปไหนต่อ** ⇒ หน้าแรกของเว็บใช้ไม่ได้
     *    สำหรับทุกคนที่ไม่ได้มาจาก LINE
     *
     * ⇒ `/` = พาไปหน้ารายการกิจกรรม (หน้าที่ **ดูได้โดยไม่ต้อง login**)
     *   `/liff` = LIFF endpoint อย่างเดียว — ถ้า config ฝั่ง LINE ผิด **แก้ที่ LINE Console**
     *   ไม่ใช่แก้ที่โค้ดจนหน้าแรกใช้ไม่ได้
     *
     * ℹ️ ใช้ `loader` + `redirect` ไม่ใช่ `<Navigate>` — ไม่ต้องโหลด chunk และไม่ต้อง
     *    render component ทิ้ง · URL ที่ผู้ใช้เห็นกลายเป็น `/activities` จริง (ไม่ใช่ `/` ค้าง)
     */
    { path: '/', loader: () => redirect(ACTIVITIES_PATH) },
    {
      path: '/liff',
      lazy: async () => {
        const { LiffEntryRoute } = await import('./liff-entry.route.js');
        return {
          element: (
            <LiffEntryRoute
              liff={deps.liff}
              api={deps.authApi}
              navigation={deps.navigation}
              onAuthenticated={deps.onAuthenticated}
            />
          ),
        };
      },
    },
    {
      path: LOGIN_PATH,
      lazy: async () => {
        const { LoginRoute } = await import('./login.route.js');
        return {
          element: (
            <LoginRoute
              google={deps.google}
              api={deps.authApi}
              navigation={deps.navigation}
              onAuthenticated={deps.onAuthenticated}
            />
          ),
        };
      },
    },
    {
      path: VERIFY_EMAIL_PATH,
      lazy: async () => {
        const { VerifyEmailRoute } = await import('./verify-email.route.js');
        return {
          element: <VerifyEmailRoute api={deps.authApi} navigation={deps.navigation} />,
        };
      },
    },
    /**
     * 🔴 **2 path นี้ถูกแชร์ออกไปนอกระบบแล้ว** (ลิงก์ในแชท LINE) — อ่านค่าจาก feature
     *    เหมือน `VERIFY_EMAIL_PATH` · เปลี่ยนเอง = ลิงก์ที่ส่งไปแล้วตายโดยไม่มีอะไรฟ้อง
     */
    {
      path: ACTIVITIES_PATH,
      lazy: async () => {
        const { ActivitiesRoute } = await import('./activities.route.js');
        return { element: <ActivitiesRoute api={deps.activitiesApi} /> };
      },
    },
    {
      path: ACTIVITY_DETAIL_PATH,
      lazy: async () => {
        const { ActivityDetailRoute } = await import('./activity-detail.route.js');
        return {
          element: (
            <ActivityDetailRoute
              api={deps.activitiesApi}
              clipboard={deps.clipboard}
              share={deps.share}
              webOrigin={deps.webOrigin}
            />
          ),
        };
      },
    },
    { path: '*', element: <NotFoundRoute /> },
  ];
}
